import { Plus, X } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { plural } from "../../shared/words";
import { useConfirm } from "../../web/components/Confirm";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { ErrorNotice, Notice, Spinner } from "../../web/components/ui";
import { can, type NameUpdates, usePriceListSaver } from "./data";
import type { PriceListData } from "./meta";
import {
    type ChoiceTag,
    composeName,
    type IncludeTag,
    type LineTag,
    type NameTagConfig,
    nameTagProblem,
    nameUpdate,
    newId,
    splitName,
    TAG_PRESETS,
    withHistory,
} from "./names";
import { NamePreview } from "./NameTags";

/*
 * Name tags in Price List settings: the choices, includes / excludes and lines this business adds to item
 * names (names.ts). Edits are a draft until saved, because saving can update item names in
 * ServiceM8: a reworded or deleted tag changes the names that use it, so that's shown first.
 */

type Change = { uuid: string; from: string; to: string };

/** Active items whose names read differently with these tags, and what they'd become. */
function changesFor(data: PriceListData, tags: NameTagConfig): Change[] {
    return data.materials
        .filter((m) => m.active)
        .flatMap((m) => {
            const to = nameUpdate(m.name, tags);
            return to === null ? [] : [{ uuid: m.uuid, from: m.name, to }];
        });
}

/** How many active items use each include, option and line value: by id, "choiceId:optionId" and "lineId=value". */
function usage(data: PriceListData) {
    const counts = new Map<string, number>();
    const add = (key: string) => counts.set(key, (counts.get(key) ?? 0) + 1);
    for (const m of data.materials) {
        if (!m.active) continue;
        const parts = splitName(m.name, data.nameTags);
        for (const [choice, option] of Object.entries(parts.choices)) add(`${choice}:${option}`);
        for (const id of [...parts.includes, ...parts.excludes]) add(id);
        for (const [id, value] of Object.entries(parts.lines)) add(`${id}=${value}`);
    }
    return (key: string) => counts.get(key) ?? 0;
}

/** "1 item" / "3 items" (nothing for none). */
const itemsText = (n: number) => (n ? `${n} ${n === 1 ? "item" : "items"}` : "");

/** An example name with the first of each tag, so the wording can be seen together. */
function exampleName(tags: NameTagConfig) {
    const parts = {
        base: "Example item",
        choices: Object.fromEntries(tags.choices.flatMap((c) => (c.options[0] ? [[c.id, c.options[0].id]] : []))),
        includes: tags.includes.slice(0, 1).map((i) => i.id),
        excludes: tags.includes.slice(1, 2).map((i) => i.id),
        lines: Object.fromEntries(tags.lines.map((l) => [l.id, l.values[0] ?? ""])),
    };
    return composeName(parts, tags);
}

export function NameTagSettings({ data }: Readonly<{ data: PriceListData }>) {
    const saver = usePriceListSaver();
    const [draft, setDraft] = useState<NameTagConfig | null>(null);
    const confirm = useConfirm();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<NameUpdates | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const used = useMemo(() => usage(data), [data]);
    const tags = draft ?? data.nameTags;
    // Edited and different from what's saved (edits undone by hand count as no change).
    const changed = draft !== null && JSON.stringify(draft) !== JSON.stringify(data.nameTags);
    const disabled = !can(data, "settings") || busy;
    const edit = (f: (t: NameTagConfig) => unknown) => {
        const next = structuredClone(tags);
        f(next);
        setDraft(next);
        setResult(null);
    };
    // Saved tags whose names still read differently (an update that failed, or wording typed in ServiceM8).
    const pending = useMemo(() => (changed ? [] : changesFor(data, data.nameTags)), [data, changed]);
    const candidate = changed ? withHistory(data.nameTags, draft) : null;
    const problem = candidate ? nameTagProblem(candidate) : null;

    const run = (work: () => Promise<NameUpdates>) => {
        setBusy(true);
        setError(null);
        work()
            .then((r) => {
                setResult(r);
                setDraft(null);
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // Saving renames the items whose names read differently with the new tags: it asks first, listing them.
    const save = async () => {
        if (!changed || !candidate || problem) return;
        const changes = changesFor(data, candidate);
        if (changes.length) {
            const yes = await confirm({
                title: `Save the name tags and update ${plural(changes.length, "item name")}?`,
                message: <ChangeList changes={changes} />,
                confirmLabel: `Save and update ${plural(changes.length, "name")}`,
            });
            if (!yes) return;
        }
        run(() => saver.saveNameTags(draft));
    };

    return (
        <div className="space-y-5">
            <p className="text-xs text-muted">
                Added to item names in ServiceM8, so quotes and invoices show them. Set them on each item (its Details). Changing or deleting one updates the
                names that use it.
            </p>
            {/* As a group shows its example: the label above, the name (on as many lines as it has) under it. */}
            <NamePreview name={exampleName(tags)} tags={tags} label="Example" />

            <Section title="Choices" help="One of a few, in brackets after the name. Worded “Includes ...” or “Excludes ...”, an option joins that list.">
                {/*
                  Two columns when there's room (wide screens), else one. Each card goes in whichever
                  column has space, so a short card beside a tall one leaves no gap.
                */}
                <div className="gap-3 xl:columns-2 [&>*]:mb-3 [&>*]:break-inside-avoid">
                    {tags.choices.map((c, ci) => (
                        <ChoiceEditor
                            key={c.id}
                            tag={c}
                            used={used}
                            disabled={disabled}
                            onChange={(f) => edit((t) => f(t.choices[ci] as ChoiceTag))}
                            onDelete={() => edit((t) => t.choices.splice(ci, 1))}
                        />
                    ))}
                </div>
                {can(data, "settings") ? <SectionAdd kind="choice" tags={tags} disabled={disabled} edit={edit} /> : null}
            </Section>

            <Section title="Includes & excludes" help="Set on each item to included, excluded or not mentioned, listed together: “Includes Keys & Programming, Excludes Batteries”.">
                <IncludeList includes={tags.includes} used={used} disabled={disabled} edit={edit} />
                {can(data, "settings") ? <SectionAdd kind="include" tags={tags} disabled={disabled} edit={edit} /> : null}
            </Section>

            <Section title="Lines" help="A line under the name, with a value: “SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)”.">
                <div className="gap-3 xl:columns-2 [&>*]:mb-3 [&>*]:break-inside-avoid">
                    {tags.lines.map((l, li) => (
                        <LineEditor
                            key={l.id}
                            tag={l}
                            used={used}
                            disabled={disabled}
                            onChange={(f) => edit((t) => f(t.lines[li] as LineTag))}
                            onDelete={() => edit((t) => t.lines.splice(li, 1))}
                        />
                    ))}
                </div>
                {can(data, "settings") ? <SectionAdd kind="line" tags={tags} disabled={disabled} edit={edit} /> : null}
            </Section>

            {changed ? (
                <SaveBar problem={problem} busy={busy} onSave={() => void save()} onCancel={() => setDraft(null)} />
            ) : null}
            {!changed && pending.length ? (
                <PendingNames
                    changes={pending}
                    updating={data.namesUpdating}
                    canEdit={can(data, "settings")}
                    busy={busy}
                    onUpdate={() => run(() => saver.updateNames())}
                />
            ) : null}
            {result ? <ResultNotice result={result} /> : null}
            <ErrorNotice error={error} />
            <LeaveGuard when={changed && !busy} message="Your changes to the name tags aren't saved yet." />
        </div>
    );
}

type Edit = (f: (t: NameTagConfig) => unknown) => void;

function IncludeList(props: Readonly<{ includes: IncludeTag[]; used: (key: string) => number; disabled: boolean; edit: Edit }>) {
    const { edit, disabled } = props;
    if (!props.includes.length) return null;
    return (
        <ul className="grid gap-2 xl:grid-cols-2">
            {props.includes.map((i, ii) => (
                <li key={i.id} className="flex items-center gap-2 rounded-md border border-line p-2">
                    <input
                        className="input min-w-0 flex-1 py-1.5 text-sm"
                        aria-label="Include or exclude"
                        value={i.name}
                        disabled={disabled}
                        onChange={(e) =>
                            edit((t) => {
                                (t.includes[ii] as IncludeTag).name = e.target.value;
                            })
                        }
                    />
                    <span className="w-16 shrink-0 text-right text-xs text-muted">{itemsText(props.used(i.id))}</span>
                    <RemoveButton label={`Delete "${i.name}"`} disabled={disabled} onClick={() => edit((t) => t.includes.splice(ii, 1))} />
                </li>
            ))}
        </ul>
    );
}

type Kind = (typeof TAG_PRESETS)[number]["kind"];

/** A blank tag of each kind. */
const blank = {
    choice: (): ChoiceTag => ({ id: newId(), label: "", options: [{ id: newId(), text: "" }] }),
    include: (): IncludeTag => ({ id: newId(), name: "" }),
    line: (): LineTag => ({ id: newId(), label: "", text: "", values: [] }),
};
const addLabels: Record<Kind, string> = { choice: "Add choice", include: "Add include / exclude", line: "Add line" };

/** Whether a ready-made tag is already there (by its name, any case). */
function hasPreset(tags: NameTagConfig, p: (typeof TAG_PRESETS)[number]) {
    const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
    if (p.kind === "choice") return tags.choices.some((c) => same(c.label, p.make().label));
    if (p.kind === "include") return tags.includes.some((i) => same(i.name, p.make().name));
    return tags.lines.some((l) => same(l.label, p.make().label));
}

/** The end of a section: add a blank tag of its kind, or one of its ready-made ones (those not already added). */
function SectionAdd({ kind, tags, disabled, edit }: Readonly<{ kind: Kind; tags: NameTagConfig; disabled: boolean; edit: Edit }>) {
    const presets = TAG_PRESETS.filter((p) => p.kind === kind && !hasPreset(tags, p));
    const add = (p: { kind: Kind; make: () => ChoiceTag | IncludeTag | LineTag }) =>
        edit((t) => {
            if (p.kind === "choice") t.choices.push(p.make() as ChoiceTag);
            else if (p.kind === "include") t.includes.push(p.make() as IncludeTag);
            else t.lines.push(p.make() as LineTag);
        });
    return (
        <div className="flex flex-wrap items-center gap-2">
            <AddButton disabled={disabled} onClick={() => add({ kind, make: blank[kind] })}>
                {addLabels[kind]}
            </AddButton>
            {presets.length ? <span className="text-xs text-muted">or ready-made:</span> : null}
            {presets.map((p) => (
                <AddButton key={p.label} disabled={disabled} onClick={() => add(p)}>
                    {p.label}
                </AddButton>
            ))}
        </div>
    );
}

/** Save or cancel the edits. When names would change, the first press lists them and the second saves. */
function SaveBar(props: Readonly<{ problem: string | null; busy: boolean; onSave: () => void; onCancel: () => void }>) {
    return (
        <div className="space-y-3 rounded-md border border-brand-500/40 bg-brand-500/5 p-3">
            {props.problem ? <Notice tone="error">{props.problem}</Notice> : null}
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="btn-primary" disabled={props.busy || Boolean(props.problem)} onClick={props.onSave}>
                    Save name tags
                </button>
                <button type="button" className="btn-secondary" disabled={props.busy} onClick={props.onCancel}>
                    Cancel
                </button>
                {props.busy ? <Spinner className="size-4" /> : null}
            </div>
        </div>
    );
}

/**
 * Names that still read differently with the saved tags: being updated now (the count drops as
 * each is saved), or not (an update that failed, or old wording typed in ServiceM8) with a retry.
 */
function PendingNames(props: Readonly<{ changes: Change[]; updating: boolean; canEdit: boolean; busy: boolean; onUpdate: () => void }>) {
    const one = props.changes.length === 1;
    if (props.updating) {
        return (
            <div className="flex items-center gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <Spinner className="size-4" />
                Updating item names in ServiceM8: {props.changes.length} left. You can leave this page.
            </div>
        );
    }
    return (
        <div className="space-y-3 rounded-md bg-warning-soft p-3 text-warning-ink">
            <p className="text-sm">
                {props.changes.length} item {one ? "name doesn't" : "names don't"} match these tags yet (old wording, or a deleted tag still in the name).
            </p>
            <ChangeList changes={props.changes} />
            {props.canEdit ? (
                <div className="flex items-center gap-2">
                    <button type="button" className="btn-secondary" disabled={props.busy} onClick={props.onUpdate}>
                        Update {one ? "it" : "them"} in ServiceM8
                    </button>
                    {props.busy ? <Spinner className="size-4" /> : null}
                </div>
            ) : null}
        </div>
    );
}

function ResultNotice({ result }: Readonly<{ result: NameUpdates }>) {
    if (!result.updating) return <Notice tone="success">Saved.</Notice>;
    return <Notice tone="success">{`Saved. Updating ${result.updating} item ${result.updating === 1 ? "name" : "names"} in ServiceM8.`}</Notice>;
}

function Section(props: Readonly<{ title: string; help: string; children: ReactNode }>) {
    return (
        <section className="space-y-2">
            <div>
                <h3 className="text-sm font-semibold">{props.title}</h3>
                <p className="text-xs text-muted">{props.help}</p>
            </div>
            {props.children}
        </section>
    );
}

function AddButton(props: Readonly<{ disabled: boolean; onClick: () => void; children: ReactNode }>) {
    return (
        <button type="button" className="btn-secondary gap-1 px-2.5 py-1 text-xs" disabled={props.disabled} onClick={props.onClick}>
            <Plus className="size-3.5" />
            {props.children}
        </button>
    );
}

function RemoveButton(props: Readonly<{ label: string; disabled: boolean; onClick: () => void }>) {
    return (
        <button
            type="button"
            className="btn-icon size-7 hover:text-danger"
            aria-label={props.label}
            title={props.label}
            disabled={props.disabled}
            onClick={props.onClick}
        >
            <X className="size-3.5" />
        </button>
    );
}

/** A list of words (a choice's options, a line's values): one input each, with remove and add. */
function WordList(
    props: Readonly<{
        items: { key: string; value: string; note?: string }[];
        addLabel: string;
        placeholder: string;
        disabled: boolean;
        onEdit: (index: number, value: string) => void;
        onRemove: (index: number) => void;
        onAdd: () => void;
    }>,
) {
    return (
        <div className="space-y-1.5">
            {props.items.map((item, i) => (
                <div key={item.key} className="flex items-center gap-2">
                    <input
                        className="input min-w-0 flex-1 py-1.5 text-sm"
                        aria-label={props.placeholder}
                        placeholder={props.placeholder}
                        value={item.value}
                        disabled={props.disabled}
                        onChange={(e) => props.onEdit(i, e.target.value)}
                    />
                    <span className="w-16 shrink-0 text-right text-xs text-muted">{item.note}</span>
                    <RemoveButton label={`Remove "${item.value}"`} disabled={props.disabled} onClick={() => props.onRemove(i)} />
                </div>
            ))}
            {props.disabled ? null : (
                <button type="button" className="link-button inline-flex items-center gap-1 text-xs" onClick={props.onAdd}>
                    <Plus className="size-3.5" /> {props.addLabel}
                </button>
            )}
        </div>
    );
}

function ChoiceEditor(
    props: Readonly<{
        tag: ChoiceTag;
        used: (key: string) => number;
        disabled: boolean;
        onChange: (f: (c: ChoiceTag) => unknown) => void;
        onDelete: () => void;
    }>,
) {
    const { tag } = props;
    return (
        <div className="space-y-3 rounded-md border border-line p-3">
            <div className="flex items-end gap-2">
                <label className="block flex-1 space-y-1">
                    <span className="block text-xs font-medium text-muted">Name</span>
                    <input
                        className="input py-1.5 text-sm"
                        placeholder="e.g. Warranty"
                        value={tag.label}
                        disabled={props.disabled}
                        onChange={(e) =>
                            props.onChange((c) => {
                                c.label = e.target.value;
                            })
                        }
                    />
                </label>
                {/* Where its options show their item counts, so its box lines up with theirs. */}
                <span className="w-16 shrink-0" />
                <RemoveButton label={`Delete "${tag.label}"`} disabled={props.disabled} onClick={props.onDelete} />
            </div>
            <div className="space-y-1">
                <span className="text-xs font-medium text-muted">Options, as they read in the name</span>
                <WordList
                    items={tag.options.map((o) => ({ key: o.id, value: o.text, note: itemsText(props.used(`${tag.id}:${o.id}`)) }))}
                    addLabel="Add option"
                    placeholder="e.g. 12 Month Warranty"
                    disabled={props.disabled}
                    onEdit={(i, v) =>
                        props.onChange((c) => {
                            (c.options[i] as ChoiceTag["options"][number]).text = v;
                        })
                    }
                    onRemove={(i) => props.onChange((c) => c.options.splice(i, 1))}
                    onAdd={() => props.onChange((c) => c.options.push({ id: newId(), text: "" }))}
                />
            </div>
        </div>
    );
}

function LineEditor(
    props: Readonly<{ tag: LineTag; used: (key: string) => number; disabled: boolean; onChange: (f: (l: LineTag) => unknown) => void; onDelete: () => void }>,
) {
    const { tag } = props;
    return (
        <div className="space-y-3 rounded-md border border-line p-3">
            <div className="flex flex-wrap items-end gap-2">
                <label className="block min-w-40 flex-1 space-y-1">
                    <span className="block text-xs font-medium text-muted">Name</span>
                    <input
                        className="input py-1.5 text-sm"
                        placeholder="e.g. Special order"
                        value={tag.label}
                        disabled={props.disabled}
                        onChange={(e) =>
                            props.onChange((l) => {
                                l.label = e.target.value;
                            })
                        }
                    />
                </label>
                <label className="block min-w-48 flex-1 space-y-1">
                    <span className="block text-xs font-medium text-muted">Line in the name</span>
                    <input
                        className="input py-1.5 text-sm"
                        placeholder="e.g. SPECIAL ORDER ITEM"
                        value={tag.text}
                        disabled={props.disabled}
                        onChange={(e) =>
                            props.onChange((l) => {
                                l.text = e.target.value;
                            })
                        }
                    />
                </label>
                <RemoveButton label={`Delete "${tag.label}"`} disabled={props.disabled} onClick={props.onDelete} />
            </div>
            <div className="space-y-1">
                <span className="text-xs font-medium text-muted">Values</span>
                <WordList
                    items={tag.values.map((v, i) => ({ key: `${i}`, value: v, note: itemsText(props.used(`${tag.id}=${v}`)) }))}
                    addLabel="Add value"
                    placeholder="e.g. 3-7 business days"
                    disabled={props.disabled}
                    onEdit={(i, v) =>
                        props.onChange((l) => {
                            l.values[i] = v;
                        })
                    }
                    onRemove={(i) => props.onChange((l) => l.values.splice(i, 1))}
                    onAdd={() => props.onChange((l) => l.values.push(""))}
                />
                {tag.values.length ? null : <p className="text-xs text-muted">No values: the line is just on or off.</p>}
            </div>
        </div>
    );
}

/** The names a change would update: the first few, before and after. */
function ChangeList({ changes }: Readonly<{ changes: Change[] }>) {
    const shown = changes.slice(0, 5);
    return (
        <div className="space-y-1 text-xs">
            <p className="font-medium">
                {changes.length} item {changes.length === 1 ? "name" : "names"} will change in ServiceM8, e.g.:
            </p>
            <ul className="space-y-1">
                {shown.map((c) => (
                    <li key={c.uuid} className="rounded bg-surface px-2 py-1 whitespace-pre-line text-ink">
                        <span className="text-muted line-through">{c.from}</span>
                        {"\n"}
                        {c.to}
                    </li>
                ))}
            </ul>
            {changes.length > shown.length ? <p className="text-muted">...and {changes.length - shown.length} more.</p> : null}
        </div>
    );
}
